import { useState, useEffect } from "react";
import { useAuth } from "../App";
import { AppLayout } from "./DashboardPage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  Settings,
  Plus,
  Trash2,
  Edit,
  Play,
  GripVertical,
  ChevronDown,
  Check,
  X,
  Zap,
  Copy,
  TestTube,
  RefreshCw,
  ArrowUpDown,
  Loader2,
  AlertCircle
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Condition builder component
function ConditionBuilder({ condition, onChange, onRemove, categories, accounts, showRemove = true }) {
  const fields = [
    { value: "description", label: "Description" },
    { value: "merchant", label: "Merchant" },
    { value: "amount", label: "Amount" },
    { value: "account_type", label: "Account Type" }
  ];

  const getOperatorsForField = (field) => {
    if (field === "amount") {
      return [
        { value: "equals", label: "Equals" },
        { value: "greater_than", label: "Greater than" },
        { value: "less_than", label: "Less than" },
        { value: "between", label: "Between" }
      ];
    }
    if (field === "account_type") {
      return [
        { value: "equals", label: "Is" },
        { value: "in", label: "Is one of" }
      ];
    }
    return [
      { value: "contains", label: "Contains" },
      { value: "equals", label: "Equals" },
      { value: "starts_with", label: "Starts with" },
      { value: "ends_with", label: "Ends with" }
    ];
  };

  const accountTypes = [
    { value: "checking", label: "Checking" },
    { value: "savings", label: "Savings" },
    { value: "credit", label: "Credit Card" },
    { value: "loan", label: "Loan" },
    { value: "investment", label: "Investment" },
    { value: "cash", label: "Cash" }
  ];

  return (
    <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50 border border-border">
      <GripVertical className="w-4 h-4 text-muted-foreground cursor-move flex-shrink-0" />
      
      <select
        value={condition.field || "description"}
        onChange={(e) => onChange({ ...condition, field: e.target.value, operator: "contains", value: "" })}
        className="h-9 rounded-md border border-input bg-background px-3 text-sm"
      >
        {fields.map(f => (
          <option key={f.value} value={f.value}>{f.label}</option>
        ))}
      </select>

      <select
        value={condition.operator || "contains"}
        onChange={(e) => onChange({ ...condition, operator: e.target.value })}
        className="h-9 rounded-md border border-input bg-background px-3 text-sm"
      >
        {getOperatorsForField(condition.field).map(op => (
          <option key={op.value} value={op.value}>{op.label}</option>
        ))}
      </select>

      {condition.field === "account_type" ? (
        <select
          value={condition.value || ""}
          onChange={(e) => onChange({ ...condition, value: e.target.value })}
          className="flex-1 h-9 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="">Select type</option>
          {accountTypes.map(t => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      ) : condition.operator === "between" ? (
        <div className="flex items-center gap-2 flex-1">
          <Input
            type="number"
            placeholder="Min"
            value={condition.min_value || ""}
            onChange={(e) => onChange({ ...condition, min_value: e.target.value })}
            className="h-9"
          />
          <span className="text-sm text-muted-foreground">to</span>
          <Input
            type="number"
            placeholder="Max"
            value={condition.max_value || ""}
            onChange={(e) => onChange({ ...condition, max_value: e.target.value })}
            className="h-9"
          />
        </div>
      ) : (
        <Input
          placeholder={condition.field === "amount" ? "0.00" : "Value..."}
          type={condition.field === "amount" ? "number" : "text"}
          value={condition.value || ""}
          onChange={(e) => onChange({ ...condition, value: e.target.value })}
          className="flex-1 h-9"
        />
      )}

      {showRemove && (
        <Button variant="ghost" size="icon" onClick={onRemove} className="h-9 w-9 flex-shrink-0">
          <X className="w-4 h-4 text-muted-foreground hover:text-destructive" />
        </Button>
      )}
    </div>
  );
}

// Action builder component
function ActionBuilder({ action, onChange, categories }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Set Category</Label>
          <select
            value={action.set_category || ""}
            onChange={(e) => onChange({ ...action, set_category: e.target.value })}
            className="w-full h-10 rounded-md border border-input bg-background px-3"
          >
            <option value="">Don't change</option>
            {categories.map(cat => (
              <option key={cat.category_id} value={cat.category_id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <Label>Set Merchant</Label>
          <Input
            placeholder="Leave empty to keep original"
            value={action.set_merchant || ""}
            onChange={(e) => onChange({ ...action, set_merchant: e.target.value })}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Set Notes</Label>
          <Input
            placeholder="Add notes to matching transactions"
            value={action.set_notes || ""}
            onChange={(e) => onChange({ ...action, set_notes: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label>Privacy Level</Label>
          <select
            value={action.set_privacy || ""}
            onChange={(e) => onChange({ ...action, set_privacy: e.target.value })}
            className="w-full h-10 rounded-md border border-input bg-background px-3"
          >
            <option value="">Don't change</option>
            <option value="shared">Shared</option>
            <option value="private">Private</option>
            <option value="totals_only">Totals Only</option>
          </select>
        </div>
      </div>

      <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/50">
        <div className="flex items-center gap-2">
          <Switch
            checked={action.mark_transfer || false}
            onCheckedChange={(checked) => onChange({ ...action, mark_transfer: checked })}
          />
          <Label>Mark as Transfer</Label>
        </div>
      </div>
    </div>
  );
}

export default function RulesPage() {
  const { household, accounts } = useAuth();
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [testResults, setTestResults] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [rerunLoading, setRerunLoading] = useState(false);

  // New rule form state
  const [newRule, setNewRule] = useState({
    name: "",
    conditions: [{ field: "description", operator: "contains", value: "" }],
    logic: "and",
    action: {},
    is_active: true
  });

  useEffect(() => {
    if (!household) return;
    fetchData();
  }, [household]);

  const fetchData = async () => {
    try {
      const [rulesRes, catRes] = await Promise.all([
        fetch(`${API_URL}/api/households/${household.household_id}/rules`, { credentials: 'include' }),
        fetch(`${API_URL}/api/households/${household.household_id}/categories`, { credentials: 'include' })
      ]);

      if (rulesRes.ok) setRules(await rulesRes.json());
      if (catRes.ok) setCategories(await catRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setNewRule({
      name: "",
      conditions: [{ field: "description", operator: "contains", value: "" }],
      logic: "and",
      action: {},
      is_active: true
    });
    setTestResults(null);
  };

  const handleCreateRule = async () => {
    const validConditions = newRule.conditions.filter(c => c.value || c.min_value || c.max_value);
    if (validConditions.length === 0) {
      toast.error("Please add at least one condition with a value");
      return;
    }

    if (!newRule.action.set_category && !newRule.action.set_merchant && !newRule.action.mark_transfer) {
      toast.error("Please specify at least one action");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/households/${household.household_id}/rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: newRule.name || `Rule ${Date.now()}`,
          priority: rules.length + 1,
          match_logic: {
            conditions: validConditions,
            logic: newRule.logic
          },
          action: newRule.action,
          is_active: newRule.is_active
        })
      });

      if (!response.ok) throw new Error('Failed to create rule');

      toast.success("Rule created");
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleUpdateRule = async () => {
    if (!editingRule) return;

    const validConditions = newRule.conditions.filter(c => c.value || c.min_value || c.max_value);

    try {
      const response = await fetch(`${API_URL}/api/households/${household.household_id}/rules/${editingRule.rule_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: newRule.name,
          match_logic: {
            conditions: validConditions,
            logic: newRule.logic
          },
          action: newRule.action,
          is_active: newRule.is_active
        })
      });

      if (!response.ok) throw new Error('Failed to update rule');

      toast.success("Rule updated");
      setEditingRule(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    try {
      await fetch(`${API_URL}/api/households/${household.household_id}/rules/${ruleId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      toast.success("Rule deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete rule");
    }
  };

  const handleToggleRule = async (rule) => {
    try {
      await fetch(`${API_URL}/api/households/${household.household_id}/rules/${rule.rule_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_active: !rule.is_active })
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to toggle rule");
    }
  };

  const handleTestRule = async () => {
    const validConditions = newRule.conditions.filter(c => c.value || c.min_value || c.max_value);
    if (validConditions.length === 0) {
      toast.error("Add conditions first");
      return;
    }

    setTestLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/households/${household.household_id}/rules/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          match_logic: {
            conditions: validConditions,
            logic: newRule.logic
          },
          limit: 10
        })
      });

      if (response.ok) {
        const data = await response.json();
        setTestResults(data);
      }
    } catch (error) {
      toast.error("Test failed");
    } finally {
      setTestLoading(false);
    }
  };

  const handleRerunRules = async (onlyUncategorized = false) => {
    setRerunLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/households/${household.household_id}/rules/rerun`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ only_uncategorized: onlyUncategorized })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Re-applied rules: ${data.matched} matched, ${data.updated} updated`);
        fetchData();
      }
    } catch (error) {
      toast.error("Re-run failed");
    } finally {
      setRerunLoading(false);
    }
  };

  const startEditingRule = (rule) => {
    const conditions = rule.match_logic?.conditions || [rule.match_logic];
    setNewRule({
      name: rule.name || "",
      conditions: conditions,
      logic: rule.match_logic?.logic || "and",
      action: rule.action || {},
      is_active: rule.is_active !== false
    });
    setEditingRule(rule);
    setShowCreateDialog(true);
  };

  const addCondition = () => {
    setNewRule({
      ...newRule,
      conditions: [...newRule.conditions, { field: "description", operator: "contains", value: "" }]
    });
  };

  const updateCondition = (index, condition) => {
    const conditions = [...newRule.conditions];
    conditions[index] = condition;
    setNewRule({ ...newRule, conditions });
  };

  const removeCondition = (index) => {
    if (newRule.conditions.length <= 1) return;
    const conditions = newRule.conditions.filter((_, i) => i !== index);
    setNewRule({ ...newRule, conditions });
  };

  const getCategoryName = (catId) => {
    const cat = categories.find(c => c.category_id === catId);
    return cat?.name || 'Unknown';
  };

  const formatCurrency = (amount) => {
    const currency = household?.currency || 'NZD';
    return new Intl.NumberFormat('en-NZ', { style: 'currency', currency }).format(amount);
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6 lg:p-8" data-testid="rules-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-3xl font-bold">Transaction Rules</h1>
            <p className="text-muted-foreground">Automatically categorize transactions based on patterns</p>
          </div>
          
          <div className="flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={rerunLoading} data-testid="rerun-rules-btn">
                  {rerunLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Re-run Rules
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => handleRerunRules(false)}>
                  <Zap className="w-4 h-4 mr-2" />
                  All pending transactions
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleRerunRules(true)}>
                  <AlertCircle className="w-4 h-4 mr-2" />
                  Only uncategorized
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Dialog open={showCreateDialog} onOpenChange={(open) => {
              setShowCreateDialog(open);
              if (!open) {
                setEditingRule(null);
                resetForm();
              }
            }}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="create-rule-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Rule
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingRule ? 'Edit Rule' : 'Create New Rule'}</DialogTitle>
                </DialogHeader>
                
                <div className="space-y-6 py-4">
                  {/* Rule Name */}
                  <div className="space-y-2">
                    <Label>Rule Name</Label>
                    <Input
                      value={newRule.name}
                      onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
                      placeholder="e.g., Groceries - Countdown"
                      data-testid="rule-name-input"
                    />
                  </div>

                  {/* Conditions */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>When transaction matches</Label>
                      {newRule.conditions.length > 1 && (
                        <select
                          value={newRule.logic}
                          onChange={(e) => setNewRule({ ...newRule, logic: e.target.value })}
                          className="h-8 rounded-md border border-input bg-background px-2 text-sm"
                        >
                          <option value="and">ALL conditions (AND)</option>
                          <option value="or">ANY condition (OR)</option>
                        </select>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      {newRule.conditions.map((condition, index) => (
                        <ConditionBuilder
                          key={index}
                          condition={condition}
                          onChange={(c) => updateCondition(index, c)}
                          onRemove={() => removeCondition(index)}
                          categories={categories}
                          accounts={accounts}
                          showRemove={newRule.conditions.length > 1}
                        />
                      ))}
                    </div>

                    <Button variant="outline" size="sm" onClick={addCondition} data-testid="add-condition-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Condition
                    </Button>
                  </div>

                  {/* Actions */}
                  <div className="space-y-3">
                    <Label>Then apply these actions</Label>
                    <ActionBuilder
                      action={newRule.action}
                      onChange={(action) => setNewRule({ ...newRule, action })}
                      categories={categories}
                    />
                  </div>

                  {/* Active Toggle */}
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50">
                    <Switch
                      checked={newRule.is_active}
                      onCheckedChange={(checked) => setNewRule({ ...newRule, is_active: checked })}
                    />
                    <div>
                      <Label>Rule Active</Label>
                      <p className="text-xs text-muted-foreground">Inactive rules won't be applied to transactions</p>
                    </div>
                  </div>

                  {/* Test Results */}
                  {testResults && (
                    <div className="p-4 rounded-lg border border-border bg-muted/30">
                      <div className="flex items-center justify-between mb-3">
                        <p className="font-medium">Test Results</p>
                        <Badge variant="secondary">{testResults.matching_count} matches</Badge>
                      </div>
                      {testResults.sample_matches.length > 0 ? (
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {testResults.sample_matches.map((tx, i) => (
                            <div key={i} className="flex items-center justify-between text-sm p-2 rounded bg-background">
                              <span className="truncate flex-1">{tx.description}</span>
                              <span className="font-medium tabular-nums ml-2">
                                {formatCurrency(tx.amount)}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">No matching transactions found</p>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <Button 
                      variant="outline" 
                      onClick={handleTestRule}
                      disabled={testLoading}
                      data-testid="test-rule-btn"
                    >
                      {testLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TestTube className="w-4 h-4 mr-2" />}
                      Test Rule
                    </Button>
                    
                    <div className="flex gap-3">
                      <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                        Cancel
                      </Button>
                      <Button 
                        onClick={editingRule ? handleUpdateRule : handleCreateRule} 
                        className="btn-primary"
                        data-testid="save-rule-btn"
                      >
                        {editingRule ? 'Update Rule' : 'Create Rule'}
                      </Button>
                    </div>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Rules List */}
        <div className="card-dashboard">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold">Active Rules</h3>
            <span className="text-sm text-muted-foreground">{rules.length} rules</span>
          </div>

          {rules.length === 0 ? (
            <div className="text-center py-12">
              <Settings className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-heading font-semibold mb-2">No Rules Yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create rules to automatically categorize your transactions
              </p>
              <Button onClick={() => setShowCreateDialog(true)}>Create Your First Rule</Button>
            </div>
          ) : (
            <div className="space-y-2">
              {rules.map((rule, index) => {
                const conditions = rule.match_logic?.conditions || [rule.match_logic];
                const hasCategory = rule.action?.set_category;
                const categoryName = hasCategory ? getCategoryName(rule.action.set_category) : null;

                return (
                  <div 
                    key={rule.rule_id}
                    className={`flex items-center gap-4 p-4 rounded-lg border transition-colors ${
                      rule.is_active !== false
                        ? 'border-border hover:bg-muted/30'
                        : 'border-border bg-muted/30 opacity-60'
                    }`}
                    data-testid={`rule-${rule.rule_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <GripVertical className="w-4 h-4 text-muted-foreground cursor-move" />
                      <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-sm font-medium">
                        {index + 1}
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium truncate">{rule.name || `Rule ${rule.rule_id.slice(-6)}`}</p>
                        {rule.is_active === false && (
                          <Badge variant="secondary" className="text-xs">Inactive</Badge>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        <span className="text-emerald-600">
                          {conditions.map(c => `${c.field} ${c.operator} "${c.value || c.min_value || ''}"`).join(` ${rule.match_logic?.logic?.toUpperCase() || 'AND'} `)}
                        </span>
                        {' → '}
                        {categoryName && <span>Category: {categoryName}</span>}
                        {rule.action?.mark_transfer && <span> (Transfer)</span>}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="tabular-nums">
                        {rule.match_count || 0} matches
                      </Badge>
                      
                      <Switch
                        checked={rule.is_active !== false}
                        onCheckedChange={() => handleToggleRule(rule)}
                      />

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <ChevronDown className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => startEditingRule(rule)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => {
                            setNewRule({
                              name: `${rule.name || 'Rule'} (Copy)`,
                              conditions: rule.match_logic?.conditions || [rule.match_logic],
                              logic: rule.match_logic?.logic || "and",
                              action: { ...rule.action },
                              is_active: true
                            });
                            setShowCreateDialog(true);
                          }}>
                            <Copy className="w-4 h-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-destructive">
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Rule?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete this rule. This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDeleteRule(rule.rule_id)}>
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
